// Planar geometry in metres. Stereo 70 (EPSG:3844) is already planar, so a
// cadastral plan's coordinates go in unchanged.
import ClipperLib from "clipper-lib";

export type Pt = [number, number];
export type Ring = Pt[];
export type Poly = Ring[]; // outer ring, then holes
export type MultiPoly = Poly[];

export const EMPTY: MultiPoly = [];

/** Signed area of a ring: positive when counter-clockwise. */
export function ringArea(r: Ring): number {
  let s = 0;
  for (let i = 0; i < r.length; i++) {
    const [x1, y1] = r[i];
    const [x2, y2] = r[(i + 1) % r.length];
    s += x1 * y2 - x2 * y1;
  }
  return s / 2;
}

/** Area of a multipolygon: outer rings minus holes. */
export function area(mp: MultiPoly): number {
  let s = 0;
  for (const poly of mp) {
    poly.forEach((ring, i) => {
      const a = Math.abs(ringArea(ring));
      s += i === 0 ? a : -a;
    });
  }
  return s;
}

/** The ring without its closing point, counter-clockwise. */
export function normalise(r: Ring): Ring {
  const open =
    r.length > 1 && r[0][0] === r[r.length - 1][0] && r[0][1] === r[r.length - 1][1]
      ? r.slice(0, -1)
      : r.slice();
  return ringArea(open) < 0 ? open.reverse() : open;
}

// Booleans go through Clipper on integer millimetres. Floating-point sweep
// libraries (polygon-clipping was the first try) fail on exactly the inputs
// this code makes: many capsules whose arcs nearly touch along one edge.
const SCALE = 1000;
type IPath = { X: number; Y: number }[];

function toPaths(mp: MultiPoly): IPath[] {
  const out: IPath[] = [];
  for (const poly of mp) for (const ring of poly) {
    out.push(ring.map(([x, y]) => ({ X: Math.round(x * SCALE), Y: Math.round(y * SCALE) })));
  }
  return out;
}

const fromPath = (p: IPath): Ring => p.map(({ X, Y }) => [X / SCALE, Y / SCALE] as Pt);

/**
 * A ring without spikes: a vertex whose two edges run back along each other
 * (out to a point and back, zero width), and repeated points. Clipper leaves
 * them where pieces cut by different booleans meet along a line, and a later
 * union reads the spike's far side as a hole: a retail park's canopy
 * "unioned" with its building came out as a hole in it. Only `clean` uses
 * it: the residential layout is tuned on the booleans as they are, and
 * cleaning every result moved its fuzz.
 */
function despike(p: IPath): IPath {
  const r = p.slice();
  for (let changed = true; changed && r.length >= 3;) {
    changed = false;
    for (let i = 0; i < r.length && r.length >= 3; i++) {
      const a = r[(i - 1 + r.length) % r.length], b = r[i], c = r[(i + 1) % r.length];
      const ux = b.X - a.X, uy = b.Y - a.Y, vx = c.X - b.X, vy = c.Y - b.Y;
      const same = ux === 0 && uy === 0;
      // back along itself: anti-parallel, the far end of the longer edge within half a millimetre of the shorter one's line
      const back = ux * vx + uy * vy < 0 && Math.abs(ux * vy - uy * vx) <= 0.5 * Math.min(Math.hypot(ux, uy), Math.hypot(vx, vy));
      if (same || back) { r.splice(i, 1); changed = true; i--; }
    }
  }
  return r;
}

/**
 * Clipper's output rings to polygons. Its PolyTree would nest them, and
 * mostly does, but when many contours touch at vertices (a difference with
 * hundreds of parking stalls) it hangs a hole on the wrong outline or drops
 * it. The flat rings are always right, outlines counter-clockwise and holes
 * clockwise, so the nesting is done here: each hole belongs to the smallest
 * outline larger than itself that holds the material just outside it.
 */
function nest(paths: IPath[], spikes = false): MultiPoly {
  const rings = paths.map((p) => fromPath(spikes ? despike(p) : p)).filter((r) => r.length >= 3);
  const outers = rings.filter((r) => ringArea(r) > 0);
  const polys: Poly[] = outers.map((r) => [normalise(r)]);
  const sizes = outers.map((r) => ringArea(r));
  for (const h of rings.filter((r) => ringArea(r) < 0)) {
    // A point a tenth of a millimetre off one of the hole's edges, on its
    // left: the hole is clockwise, so that is outside it. A hole may touch its
    // outline along an edge, where that point falls outside both; try the
    // next edge then.
    const size = -ringArea(h);
    let k = -1;
    for (let e = 0; e < h.length && k < 0; e++) {
      const a = h[e], b = h[(e + 1) % h.length];
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (L < 1e-3) continue;
      const q: Pt = [(a[0] + b[0]) / 2 - ((b[1] - a[1]) / L) * 1e-4, (a[1] + b[1]) / 2 + ((b[0] - a[0]) / L) * 1e-4];
      outers.forEach((o, i) => { if (sizes[i] > size && (k < 0 || sizes[i] < sizes[k]) && inRing(q, o)) k = i; });
    }
    if (k >= 0) polys[k].push(normalise(h).reverse());
  }
  return polys;
}

function run(type: ClipperLib.ClipType, subject: MultiPoly[], clip: MultiPoly[] = []): MultiPoly {
  const c = new ClipperLib.Clipper();
  for (const s of subject) c.AddPaths(toPaths(s), ClipperLib.PolyType.ptSubject, true);
  for (const k of clip) c.AddPaths(toPaths(k), ClipperLib.PolyType.ptClip, true);
  const out: IPath[] = [];
  // Union of a subject with itself under non-zero fill dissolves overlaps
  // between the capsules, which is the whole point of the union.
  c.Execute(type, out, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
  return nest(out);
}

export const union = (...g: MultiPoly[]): MultiPoly =>
  g.length === 0 ? [] : run(ClipperLib.ClipType.ctUnion, g);
export const intersection = (a: MultiPoly, b: MultiPoly): MultiPoly =>
  a.length === 0 || b.length === 0 ? [] : run(ClipperLib.ClipType.ctIntersection, [a], [b]);
export const difference = (a: MultiPoly, ...b: MultiPoly[]): MultiPoly => {
  const cut = b.filter((m) => m.length > 0);
  return a.length === 0 ? [] : cut.length === 0 ? a : run(ClipperLib.ClipType.ctDifference, [a], cut);
};

/** The same shape without zero-width spikes: for geometry that goes on into more booleans. */
export const clean = (mp: MultiPoly): MultiPoly => (mp.length ? nest(toPaths(mp), true) : mp);

/**
 * Every point within `r` of the segment a-b: a rectangle with round caps.
 *
 * Distance to the *segment*, not to the line, is what a setback means at a
 * corner: a point is too close if it is too close to any part of the
 * boundary. At a reflex corner of the plot that nearest part is the vertex,
 * so the permitted area wraps round it in an arc.
 */
export function capsule(a: Pt, b: Pt, r: number, arcSegments = 8): MultiPoly {
  if (r <= 0) return [];
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (len === 0) return [];
  const ang = Math.atan2(dy, dx);
  const ring: Ring = [];
  // cap around b, from the right-hand side to the left-hand side
  for (let i = 0; i <= arcSegments; i++) {
    const t = ang - Math.PI / 2 + (Math.PI * i) / arcSegments;
    ring.push([b[0] + r * Math.cos(t), b[1] + r * Math.sin(t)]);
  }
  for (let i = 0; i <= arcSegments; i++) {
    const t = ang + Math.PI / 2 + (Math.PI * i) / arcSegments;
    ring.push([a[0] + r * Math.cos(t), a[1] + r * Math.sin(t)]);
  }
  return [[ring]];
}

/** Edges of a ring as [start, end] pairs; edge i runs from r[i] to r[i+1]. */
export function edges(r: Ring): [Pt, Pt][] {
  return r.map((p, i) => [p, r[(i + 1) % r.length]] as [Pt, Pt]);
}

/**
 * `mp` grown by `d` metres (mitred corners). Pieces built by different
 * booleans can share an edge and still differ by a millimetre of rounding;
 * growing by a centimetre before a union is how to ask whether they touch.
 */
export function grow(mp: MultiPoly, d: number): MultiPoly {
  const co = new ClipperLib.ClipperOffset(2, 0.25);
  co.AddPaths(toPaths(mp), ClipperLib.JoinType.jtMiter, ClipperLib.EndType.etClosedPolygon);
  const out: IPath[] = [];
  co.Execute(out, d * SCALE);
  return nest(out);
}

/**
 * The union of pieces that meet edge to edge, as one: grown by a centimetre,
 * united, shrunk back. Pieces cut by different booleans share edges only to
 * within a millimetre, and a bare union leaves them apart.
 */
export function join(...g: MultiPoly[]): MultiPoly {
  const parts = g.filter((m) => m.length);
  if (!parts.length) return [];
  return grow(union(...parts.map((m) => grow(m, 0.01))), -0.01);
}

/** Everything in `mp` at least `d` from all of its own boundaries. */
export function inset(mp: MultiPoly, d: number): MultiPoly {
  if (d <= 0) return mp;
  const bands: MultiPoly[] = [];
  for (const poly of mp) for (const ring of poly) for (const [a, b] of edges(ring)) bands.push(capsule(a, b, d));
  return difference(mp, union(...bands));
}

/**
 * The strip of the plane on the inner side of the line through a-b, from
 * distance `from` to distance `to`, long enough to cover anything nearby.
 */
export function strip(a: Pt, b: Pt, from: number, to: number, reach = 10_000): MultiPoly {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  const ux = dx / len;
  const uy = dy / len;
  // counter-clockwise ring: the inside is on the left
  const nx = -uy;
  const ny = ux;
  const p = (s: number, t: number): Pt => [a[0] + ux * s + nx * t, a[1] + uy * s + ny * t];
  return [[[p(-reach, from), p(len + reach, from), p(len + reach, to), p(-reach, to)]]];
}

export function bbox(mp: MultiPoly): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const poly of mp) for (const [x, y] of poly[0]) {
    minX = Math.min(minX, x); minY = Math.min(minY, y);
    maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  }
  return { minX, minY, maxX, maxY };
}

function nearest(p: Pt, mp: MultiPoly): { edge: number; inside: boolean } {
  let edge = Infinity;
  let inside = false;
  for (const poly of mp) {
    let inPoly = false;
    poly.forEach((ring, ri) => {
      let crossings = false;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i], [xj, yj] = ring[j];
        if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) crossings = !crossings;
        const dx = xi - xj, dy = yi - yj;
        const len2 = dx * dx + dy * dy;
        const t = len2 ? Math.max(0, Math.min(1, ((p[0] - xj) * dx + (p[1] - yj) * dy) / len2)) : 0;
        edge = Math.min(edge, Math.hypot(p[0] - (xj + t * dx), p[1] - (yj + t * dy)));
      }
      if (ri === 0) inPoly = crossings;
      else if (crossings) inPoly = false; // inside a hole
    });
    inside ||= inPoly;
  }
  return { edge, inside };
}

// A ring's bounding box, kept with the ring: point tests run millions of
// times against the same few outlines (every corner of every candidate stall).
const boxes = new WeakMap<Ring, [number, number, number, number]>();
function boxOf(r: Ring) {
  let b = boxes.get(r);
  if (!b) {
    b = [Infinity, Infinity, -Infinity, -Infinity];
    for (const [x, y] of r) { if (x < b[0]) b[0] = x; if (y < b[1]) b[1] = y; if (x > b[2]) b[2] = x; if (y > b[3]) b[3] = y; }
    boxes.set(r, b);
  }
  return b;
}
function inRing(p: Pt, ring: Ring): boolean {
  const b = boxOf(ring);
  if (p[0] < b[0] || p[0] > b[2] || p[1] < b[1] || p[1] > b[3]) return false;
  let c = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
}
/** Whether a point is inside a multipolygon (and not in one of its holes). */
export function contains(p: Pt, mp: MultiPoly): boolean {
  return mp.some((poly) => inRing(p, poly[0]) && !poly.slice(1).some((h) => inRing(p, h)));
}

/**
 * Inside or on the outline, to a nanometre: the same answer as
 * `distanceTo(p, mp) === 0`, without measuring the distance of a point the
 * bounding boxes already put outside.
 */
export function covers(p: Pt, mp: MultiPoly): boolean {
  if (contains(p, mp)) return true;
  const near = mp.some((poly) => { const b = boxOf(poly[0]); return p[0] >= b[0] - 1e-9 && p[0] <= b[2] + 1e-9 && p[1] >= b[1] - 1e-9 && p[1] <= b[3] + 1e-9; });
  return near && nearest(p, mp).edge === 0;
}

/** Distance from a point to a multipolygon: 0 inside it. */
export function distanceTo(p: Pt, mp: MultiPoly): number {
  return contains(p, mp) ? 0 : nearest(p, mp).edge;
}

/** Distance from a point to the outline of a multipolygon, from either side. */
export function distanceToOutline(p: Pt, mp: MultiPoly): number {
  return nearest(p, mp).edge;
}

/** The point of a multipolygon's outline nearest to p. */
export function nearestOnOutline(p: Pt, mp: MultiPoly): Pt {
  let best: Pt = p, bestD = Infinity;
  for (const poly of mp) for (const ring of poly) for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [ax, ay] = ring[j], [bx, by] = ring[i];
    const dx = bx - ax, dy = by - ay, len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - ay) * dy) / len2)) : 0;
    const q: Pt = [ax + t * dx, ay + t * dy];
    const d = Math.hypot(q[0] - p[0], q[1] - p[1]);
    if (d < bestD) { bestD = d; best = q; }
  }
  return best;
}

export const round = (n: number, dp = 2) => Math.round(n * 10 ** dp) / 10 ** dp;
